import React, { useState } from 'react';
import { HiCpuChip } from 'react-icons/hi2';;
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Icon } from '@/shared/icons'
/**
 * Form for connecting Telegram HiCpuChip
 *
 * Flow: Collect bot token → Test connection → Show webhook configuration
 *
 * @example
 * <TelegramBotForm
 *   onComplete={(data) => handleAuthComplete(data)}
 * />
 */
export interface TelegramBotFormProps {
  /** Callback when authentication is complete */
  onComplete: (data: TelegramBotData) => void;
}

export interface TelegramBotData {
  providerId: string;
  name: string;
  identifier: string;
  credentials: {
    botToken: string;
  };
  config: {
    username: string;
  };
}

type ConnectionStep = 'credentials' | 'connecting' | 'complete';

export const TelegramBotForm: React.FC<TelegramBotFormProps> = ({
  onComplete,
}) => {
  const [step, setStep] = useState<ConnectionStep>('credentials');
  const [channelName, setChannelName] = useState('');
  const [botToken, setBotToken] = useState('');
  const [botUsername, setBotUsername] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleConnect = async () => {
    // ✅ Validação completa conforme backend
    if (!channelName || !botToken) {
      setErrorMessage('Preencha todos os campos obrigatórios');
      return;
    }

    if (channelName.trim().length < 2) {
      setErrorMessage('Nome do canal deve ter pelo menos 2 caracteres');
      return;
    }

    if (botToken.trim().length === 0) {
      setErrorMessage('HiCpuChip Token é obrigatório');
      return;
    }

    setErrorMessage(null);
    setStep('connecting');

    // TODO: Call backend API to validate bot token and get bot info
    setTimeout(() => {
      // Mock bot username (should come from backend after validating token)
      setBotUsername('@support_bot');
      setStep('complete');
    }, 2000);
  };

  const handleComplete = () => {
    onComplete({
      providerId: 'telegram-bot',
      name: channelName,
      identifier: botUsername || botToken,
      credentials: {
        botToken,
      },
      config: {
        username: botUsername,
      },
    });
  };

  return (
    <div className="space-y-6">
      {/* Step 1: HiCpuChip Token Input */}
      {step === 'credentials' && (
        <div className="space-y-4">
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Nome do Canal *
            </label>
            <Input
              type="text"
              value={channelName}
              onChange={(e) => setChannelName(e.target.value)}
              placeholder="Ex: Telegram HiCpuChip Suporte"
              className="w-full"
            />
          </div>

          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              HiCpuChip Token *
            </label>
            <div className="relative">
              <Icon name="key" size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10 pointer-events-none"
                style={{ color: 'var(--theme-card-muted)' }} />
              <Input
                type="password"
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                className="w-full pl-10"
              />
            </div>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Token fornecido pelo @BotFather no Telegram
            </p>
          </div>

          {/* Instructions */}
          <div
            className="p-4 rounded-lg"
            style={{
              backgroundColor: 'var(--theme-info-background)',
              border: '1px solid var(--theme-info-color)',
            }}
          >
            <p
              className="text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Como criar um bot no Telegram:
            </p>
            <ol
              className="text-xs space-y-1 list-decimal list-inside mb-2"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              <li>Abra o Telegram e procure por @BotFather</li>
              <li>Envie o comando /newbot</li>
              <li>Escolha um nome para seu bot</li>
              <li>Escolha um username (deve terminar com 'bot')</li>
              <li>Copie o token fornecido e cole acima</li>
            </ol>
            <a
              href="https://core.telegram.org/bots/tutorial"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm flex items-center gap-1 hover:underline"
              style={{ color: 'var(--theme-info-color)' }}
            >
              Ver documentação completa
              <Icon name="external-link" size={14} />
            </a>
          </div>

          {errorMessage && (
            <div
              className="p-3 rounded-lg"
              style={{
                backgroundColor: 'var(--theme-error-background)',
                border: '1px solid var(--theme-error-color)',
              }}
            >
              <p
                className="text-sm"
                style={{ color: 'var(--theme-error-color)' }}
              >
                {errorMessage}
              </p>
            </div>
          )}

          <Button
            onClick={handleConnect}
            className="w-full"
          >
            Conectar HiCpuChip do Telegram
          </Button>
        </div>
      )}

      {/* Step 2: Connecting */}
      {step === 'connecting' && (
        <div className="text-center py-8">
          <div className="inline-flex items-center justify-center mb-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center animate-pulse"
              style={{ backgroundColor: 'var(--theme-button-primary)' }}
            >
              <HiCpuChip size={32} className="text-white" />
            </div>
          </div>
          <p
            className="text-lg font-medium"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Conectando ao Telegram HiCpuChip...
          </p>
          <p
            className="text-sm mt-2"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Validando token e configurando webhook
          </p>
        </div>
      )}

      {/* Step 3: Connection Successful */}
      {step === 'complete' && (
        <div className="text-center py-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{ backgroundColor: 'var(--theme-success-color)' }}
          >
            <Icon name="check-circle" size={32} className="text-white" />
          </div>
          <p
            className="text-lg font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            HiCpuChip conectado com sucesso!
          </p>
          <p
            className="text-sm mb-2"
            style={{ color: 'var(--theme-card-muted)' }}
          >
            Seu bot {botUsername} está pronto para receber mensagens
          </p>

          <div
            className="mt-6 p-4 rounded-lg text-left"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)',
            }}
          >
            <p
              className="text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Próximos passos:
            </p>
            <ul
              className="text-sm space-y-1 list-disc list-inside"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              <li>Configure as mensagens de boas-vindas</li>
              <li>Defina os comandos disponíveis (/start, /help, etc.)</li>
              <li>Configure as regras de atribuição de operadores</li>
              <li>Teste enviando uma mensagem para {botUsername}</li>
            </ul>
          </div>

          <Button
            onClick={handleComplete}
            className="w-full mt-6"
          >
            Concluir Configuração
          </Button>
        </div>
      )}
    </div>
  );
};

TelegramBotForm.displayName = 'TelegramBotForm';
